import React, { useEffect, useRef, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { instance, isSwarm } from '../instance';
import { routeAllowedOnInstance } from '../navigation/routeInstances';
import useUserStore from '../stores/useUserStore';
import useDashboardConfigStore from '../stores/useDashboardConfigStore';
import useNavPrefsStore from '../stores/useNavPrefsStore';
import { MegaMenu } from './MegaMenu';
import { Bookmarks } from './Bookmarks';
import { CommandPalette } from './CommandPalette';
import { useNavAlerts } from './useNavAlerts';
import { NotificationBell } from './NotificationBell';
import { MobileMenu } from './MobileMenu';
import { Icon } from './navIcons';
import { NavDot } from './NavDot';
import FlerpieLauncher from '../components/ChatBot/FlerpieLauncher';

const itemClass =
  'group flex items-center gap-2 px-3.5 py-2 text-sm text-on-surface-variant hover:text-white transition-colors duration-150';
const tick = <span className="w-0.5 h-3 rounded-full bg-primary/0 group-hover:bg-primary/60 transition-colors duration-150" />;

const UserDropdown: React.FC<{ user: any }> = ({ user }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const history = useHistory();
  const logout = useUserStore((s) => s.logout);
  const setEditMode = useDashboardConfigStore((s) => s.setEditMode);
  const flerpieHidden = useNavPrefsStore((s) => s.flerpieHidden);
  const setFlerpieHidden = useNavPrefsStore((s) => s.setFlerpieHidden);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      clearTimeout(closeTimer.current);
    };
  }, []);

  const enter = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const leave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 200);
  };
  // Open on keyboard focus entering the menu; close when focus leaves it — so
  // the (otherwise hover-only) menu is fully keyboard-operable.
  const handleBlur = (e: React.FocusEvent) => {
    if (!ref.current?.contains(e.relatedTarget as Node)) leave();
  };

  const handleCustomize = () => {
    setOpen(false);
    if (window.location.pathname !== '/') history.push('/');
    setTimeout(() => setEditMode(true), 100);
  };
  const handleLogout = async () => {
    setOpen(false);
    await logout();
  };

  // My Schedule is crew shifts and show travel — Firefleet-only, so the route
  // gate would bounce it. Same check in MobileMenu; both read the manifest.
  const canTravel =
    routeAllowedOnInstance('/my-schedule') &&
    (user.isadmin || user.can_admin || user.can_operations || user.can_producer || user.can_pilot || user.travel_tier);
  // No Flerpie on Swarm (decision D4) — see docs/operations/two-instances.md.
  const canSeeFlerpie = !isSwarm && (user.can_ai || user.isadmin || user.can_admin);
  const flerpieOn = !flerpieHidden;

  return (
    <div ref={ref} className="relative" onMouseEnter={enter} onMouseLeave={leave} onFocus={enter} onBlur={handleBlur}>
      <Link
        to="/settings"
        onClick={() => setOpen(false)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium text-on-surface-variant hover:text-white hover:bg-gray-800/60 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
      >
        {user.firstname}
        <Icon name="chevron" strokeWidth={2.4} className={`w-3 h-3 opacity-60 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </Link>
      <div
        className={`absolute top-full right-0 pt-2 z-50 transition-all duration-150 ${
          open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-1 pointer-events-none'
        }`}
      >
        <div className="relative bg-[#242424] border border-gray-700/60 rounded-lg shadow-xl shadow-black/40 py-1.5 min-w-[200px] overflow-hidden">
          <div className="absolute top-2 right-4 w-8 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
          {canTravel && (
            <Link to="/my-schedule" onClick={() => setOpen(false)} className={itemClass}>
              {tick}
              My Schedule
            </Link>
          )}
          <Link to="/settings" onClick={() => setOpen(false)} className={itemClass}>
            {tick}
            Settings
          </Link>
          <button onClick={handleCustomize} className={`w-full text-left ${itemClass}`}>
            {tick}
            Customize Dashboard
          </button>
          {canSeeFlerpie && (
            <>
              <div className="h-px bg-gray-700/40 my-1 mx-3" />
              <button
                onClick={() => setFlerpieHidden(!flerpieHidden)}
                className="group w-full flex items-center justify-between gap-2 px-3.5 py-2 text-sm text-on-surface-variant hover:text-white transition-colors duration-150"
                aria-pressed={flerpieOn}
              >
                <span className="flex items-center gap-2">
                  {tick}
                  Flerpie assistant
                </span>
                <span
                  className={`relative w-[34px] h-[20px] rounded-full border transition-colors ${
                    flerpieOn ? 'bg-primary/20 border-primary' : 'bg-gray-700/40 border-outline-variant'
                  }`}
                >
                  <span
                    className={`absolute top-[2px] left-[2px] w-[14px] h-[14px] rounded-full transition-transform duration-200 ${
                      flerpieOn ? 'translate-x-[14px] bg-primary' : 'translate-x-0 bg-on-surface-variant'
                    }`}
                  />
                </span>
              </button>
            </>
          )}
          {(user.isadmin || user.can_admin) && (
            <>
              <div className="h-px bg-gray-700/40 my-1 mx-3" />
              <Link to="/admin" onClick={() => setOpen(false)} className={itemClass}>
                {tick}
                Admin
              </Link>
            </>
          )}
          <div className="h-px bg-gray-700/40 my-1 mx-3" />
          <button
            onClick={handleLogout}
            className="group w-full flex items-center gap-2 px-3.5 py-2 text-sm text-red-400/80 hover:text-red-400 transition-colors duration-150 text-left"
          >
            <span className="w-0.5 h-3 rounded-full bg-red-400/0 group-hover:bg-red-400/60 transition-colors duration-150" />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

const NavBar: React.FC = () => {
  const user = useUserStore((s) => s.user);
  const alerts = useNavAlerts();
  const location = useLocation();
  const pushRecent = useNavPrefsStore((s) => s.pushRecent);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [highlightDomain, setHighlightDomain] = useState<string | null>(null);
  const total = Object.values(alerts).reduce((a, b) => a + b, 0);

  // track visited routes for the mobile "Jump back in" row
  useEffect(() => {
    pushRecent(location.pathname);
  }, [location.pathname, pushRecent]);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, []);

  return (
    <header className="bg-[#191919] flex items-center gap-2 px-4 py-2">
      <h1 className="flex items-center shrink-0">
        <Link to="/">
          <img src={instance.logo} alt={instance.logoAlt} className="h-8 w-auto object-contain rounded" />
        </Link>
      </h1>

      <Bookmarks user={user} />
      <MegaMenu user={user} alerts={alerts} highlightKey={highlightDomain} />

      <div className="flex-1" />

      <div className="hidden md:flex items-center gap-1">
        <button
          onClick={() => setPaletteOpen(true)}
          aria-label="Search"
          aria-haspopup="dialog"
          aria-keyshortcuts="Meta+K Control+K"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-[12.5px] text-on-surface-variant bg-[#2a2a2a] border border-gray-700/60 hover:border-outline hover:text-on-surface transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
        >
          <Icon name="search" strokeWidth={1.6} className="w-3.5 h-3.5" />
          <span className="hidden lg:inline">Search…</span>
        </button>
        <NotificationBell alerts={alerts} onHighlight={setHighlightDomain} />
        <FlerpieLauncher />
        <div className="w-px h-5 bg-gray-700 mx-1.5" />
        <UserDropdown user={user} />
      </div>

      {/* Below md the bell's group hides, so Flerpie's launcher sits here, phones included. */}
      <FlerpieLauncher className="md:hidden" />

      {/* Mobile hamburger — opens the global nav drawer */}
      <button
        onClick={() => setMobileOpen(true)}
        aria-label="Open navigation menu"
        aria-haspopup="menu"
        aria-expanded={mobileOpen}
        className="md:hidden relative w-9 h-9 rounded-md inline-flex items-center justify-center text-on-surface-variant hover:text-white hover:bg-white/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
      >
        <svg width="22" height="22" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5"
          />
        </svg>
        {total > 0 && <NavDot pulse className="absolute top-1.5 right-2 ring-2 ring-[#191919]" />}
      </button>

      <MobileMenu
        isOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        user={user}
        alerts={alerts}
        onOpenPalette={() => setPaletteOpen(true)}
      />
      <CommandPalette user={user} open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </header>
  );
};

export default NavBar;
